import { Skeleton } from "@/components/ui/baseUi/skeleton"
import { Text } from "@/components/ui/baseUi/text"
import { cn } from "@/modules/lib/utils"
import { formatPhoneNumber } from "@/modules/utils/numberUtils"
import { word } from "@modules/core/utils/i18n"
import { MemberDetailedEntity } from "@modules/members/entities/memberEntity"
import { PublicProfileDetails } from "@modules/profile/publicProfile/components/publicProfileDetails"
import type { CountryCode } from "libphonenumber-js"
import { Ambulance, Dumbbell, House, Mail, Phone } from "lucide-react"

type MemberTabProfileProps = {
  memberDetailed?: MemberDetailedEntity | null
  className?: string
} & React.ComponentProps<"div">

export function MemberTabProfile({ memberDetailed, className, ...props }: MemberTabProfileProps) {
  // SKELETON
  if (!memberDetailed) {
    return (
      <div className={cn("flex w-full flex-col gap-12 px-4", className)} {...props}>
        {/* CONTACT */}
        <div className="mt-2 flex flex-col gap-4">
          <Text fontStyle="TM" className="text-textDefault-body">
            {word("box.member.table.profile.contact")}
          </Text>
          <div className="flex">
            <div className="flex flex-1 items-center gap-2">
              <Mail strokeWidth="1" />
              <Skeleton className="w-48 rounded-sm" />
            </div>
          </div>
          {/* ADDRESS */}
          <div className="flex flex-row gap-2">
            <House strokeWidth="1" />

            <div className="flex flex-1 flex-col gap-2">
              <Text className="text-textDefault-caption uppercase" fontStyle="BS">
                {word("box.member.table.profile.address.label")}
              </Text>
              <Skeleton className="w-28 rounded-sm" />

              <div className="flex justify-between">
                <div className="flex flex-col gap-1">
                  <Text className="mt-2 text-textDefault-caption uppercase" fontStyle="BS">
                    {word("box.member.table.profile.address.zipCode")}
                  </Text>
                  <Skeleton className="w-28 rounded-sm" />
                </div>

                <div className="flex flex-col gap-1">
                  <Text className="mt-2 text-textDefault-caption uppercase" fontStyle="BS">
                    {word("box.member.table.profile.address.city")}
                  </Text>
                  <Skeleton className="w-28 rounded-sm" />
                </div>

                <div className="flex flex-col gap-1">
                  <Text className="mt-2 text-textDefault-caption uppercase" fontStyle="BS">
                    {word("box.member.table.profile.address.country")}
                  </Text>
                  <Skeleton className="w-28 rounded-sm" />
                </div>
              </div>
            </div>
          </div>

          {/* EMERGENCY CONTACT */}
          <div className="flex flex-row gap-2">
            <Ambulance strokeWidth="1" />
            <div className="flex flex-col gap-1">
              <Text className="text-textDefault-caption uppercase" fontStyle="BS">
                {word("box.member.table.profile.emergencyContact")}
              </Text>
              <Skeleton className="w-58 rounded-sm" />
            </div>
          </div>
          <div className="flex flex-row gap-2">
            <Dumbbell strokeWidth="1" />
            <div className="flex flex-col gap-1">
              <Text className="text-textDefault-caption uppercase" fontStyle="BS">
                {word("box.member.table.profile.startDate")}
              </Text>
              <Skeleton className="w-22 rounded-sm" />
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className={cn("flex w-full flex-col gap-12 px-4", className)} {...props}>
      {/* CONTACT */}
      <div className="mt-2 flex flex-col gap-4">
        <Text fontStyle="TM" className="text-textDefault-body">
          {word("box.member.table.profile.contact")}
        </Text>
        <div className="flex">
          <div className="flex flex-1 gap-2">
            <Mail strokeWidth="1" />
            <Text fontStyle="TS" className="font-semibold text-textDefault-body">
              {memberDetailed.email}
            </Text>
          </div>
          {memberDetailed.phoneWithPrefix && (
            <div className="flex gap-2">
              <Phone strokeWidth="1" />
              <Text fontStyle="TS" className="font-semibold text-textDefault-body">
                {formatPhoneNumber(
                  memberDetailed.phoneWithPrefix.number,
                  memberDetailed.phoneWithPrefix.countryCode as CountryCode,
                )}
              </Text>
            </div>
          )}
        </div>
      </div>
      {/* ADDRESS */}
      <div className="flex flex-row gap-2">
        <House strokeWidth="1" />

        <div className="mt-1 flex flex-1 flex-col gap-2">
          <Text className="text-textDefault-caption uppercase" fontStyle="BS">
            {word("box.member.table.profile.address.label")}
          </Text>
          <Text fontStyle="TS" className="text-textDefault-body">
            {memberDetailed.address?.address ? memberDetailed.address.address : word("global.notProvided")}
          </Text>

          {memberDetailed.address?.additionalInformation && (
            <>
              <Text className="mt-2 text-textDefault-caption uppercase" fontStyle="BS">
                {word("box.member.table.profile.address.supplement")}
              </Text>
              <Text fontStyle="TS" className="text-textDefault-body">
                {memberDetailed.address.additionalInformation}
              </Text>
            </>
          )}

          <div className="flex justify-between">
            <div className="flex flex-col gap-1">
              <Text className="mt-2 text-textDefault-caption uppercase" fontStyle="BS">
                {word("box.member.table.profile.address.zipCode")}
              </Text>
              <Text fontStyle="TS" className="text-textDefault-body">
                {memberDetailed.address?.postalCode ? memberDetailed.address.postalCode : word("global.notProvided")}
              </Text>
            </div>

            <div className="flex flex-col gap-1">
              <Text className="mt-2 text-textDefault-caption uppercase" fontStyle="BS">
                {word("box.member.table.profile.address.city")}
              </Text>
              <Text fontStyle="TS" className="text-textDefault-body">
                {memberDetailed.address?.city ? memberDetailed.address.city : word("global.notProvided")}
              </Text>
            </div>

            <div className="flex flex-col gap-1">
              <Text className="mt-2 text-textDefault-caption uppercase" fontStyle="BS">
                {word("box.member.table.profile.address.country")}
              </Text>
              <Text fontStyle="TS" className="text-textDefault-body">
                {memberDetailed.address?.country ? memberDetailed.address.country : word("global.notProvided")}
              </Text>
            </div>
          </div>
        </div>
      </div>

      {/* EMERGENCY CONTACT */}
      <div className="flex flex-row gap-2">
        <Ambulance strokeWidth="1" />
        <div className="mt-1 flex flex-col gap-1">
          <Text className="text-textDefault-caption uppercase" fontStyle="BS">
            {word("box.member.table.profile.emergencyContact")}
          </Text>
          {memberDetailed.emergencyInformation ? (
            <Text fontStyle="TS" className="text-textDefault-body">
              {memberDetailed.emergencyInformation}
            </Text>
          ) : (
            <Text fontStyle="TS" className="text-textDefault-body">
              {word("box.member.table.profile.emergencyContactNotProvided")}
            </Text>
          )}
        </div>
      </div>

      {memberDetailed.publicProfile && <PublicProfileDetails publicProfile={memberDetailed.publicProfile} />}
    </div>
  )
}
